<template>
    <div>
        <b-form @submit.prevent="search">
            <b-form-group>
                <b-input-group>
                    <b-form-input ref="search-term" type="search" placeholder="Search" v-model="search_term"/>
                    <b-input-group-append>
                        <b-btn type="submit" variant="outline-secondary" :disabled="searching">
                            <font-awesome-icon :icon="['fad', 'search']"/>
                        </b-btn>
                    </b-input-group-append>
                    <b-input-group-append v-if="searched">
                        <b-btn type="submit" variant="danger" @click.prevent="clearSearch">
                            <font-awesome-icon :icon="['fad', 'backspace']"/>
                        </b-btn>
                    </b-input-group-append>
                </b-input-group>
            </b-form-group>
        </b-form>

        <div v-if="searched">
            <div v-if="searching" class="mb-3">
                <b-spinner label="Spinning"></b-spinner>
            </div>
            <div v-else>
                <b-list-group v-if="worksheets && worksheets.length" class="my-3">
                    <b-list-group-item v-for="(worksheet, index) in worksheets" :key="index">
                        <div class="d-flex align-items-center">
                            <div class="mr-3">{{ worksheet.title }}</div>
                            <div v-if="worksheet.dam_id" class="my-2">
                                <div v-if="worksheet.loading">
                                    <b-spinner label="Loading..." small></b-spinner>
                                </div>
                                <b-list-group v-else>
                                    <b-list-group-item
                                        v-if="worksheet.previewpermlink"
                                        class="d-flex align-items-center"
                                    >
                                        <div>
                                            <tippy placement="right" arrow interactive :delay="[500, 0]">
                                                <template v-slot:trigger>
                                                    <b-img
                                                        :src="worksheet.thumbpermlink"
                                                        class="p-0"
                                                        width="100"
                                                        height="auto"
                                                        thumbnail
                                                    />
                                                </template>
                                                <div>
                                                    <b-link :href="worksheet.previewpermlink" target="_blank">
                                                        <b-img
                                                            :src="worksheet.previewpermlink"
                                                            class="p-0"
                                                            width="300"
                                                            height="auto" thumbnail
                                                        />
                                                    </b-link>
                                                </div>
                                            </tippy>
                                        </div>
                                    </b-list-group-item>
                                </b-list-group>
                            </div>
                            <div class="ml-auto">
                                <b-link href="#" @click.prevent="addInteractiveMediaLink(worksheet, 'worksheet')">
                                    <font-awesome-icon :icon="['fad', 'plus-square']"/>
                                </b-link>
                            </div>
                        </div>
                    </b-list-group-item>
                </b-list-group>

                <b-alert variant="danger" :show="searched && (!worksheets || worksheets && !worksheets.length)">
                    0 results
                </b-alert>
            </div>
        </div>
        <div v-else>
            <b-list-group v-if="linked_worksheets && linked_worksheets.length">
                <b-list-group-item v-for="(worksheet, index) in linked_worksheets" :key="index">
                    <div class="d-flex align-items-center">
                        <div class="mr-3">
                            <span :class="{'text-muted': !worksheet.title}">
                                {{ worksheet.title || 'no title' }}
                            </span>
                        </div>
                        <div v-if="worksheet.dam_id" class="my-2">
                            <div v-if="worksheet.loading">
                                <b-spinner label="Loading..." small></b-spinner>
                            </div>
                            <b-list-group v-else>
                                <b-list-group-item v-if="worksheet.previewpermlink" class="d-flex align-items-center">
                                    <div>
                                        <tippy placement="right" arrow interactive :delay="[500, 0]">
                                            <template v-slot:trigger>
                                                <b-img :src="worksheet.thumbpermlink" class="p-0" width="100"
                                                       height="auto" thumbnail/>
                                            </template>
                                            <div>
                                                <b-link :href="worksheet.previewpermlink" target="_blank">
                                                    <b-img
                                                        :src="worksheet.previewpermlink"
                                                        class="p-0"
                                                        width="300"
                                                        height="auto"
                                                        thumbnail
                                                    />
                                                </b-link>
                                            </div>
                                        </tippy>
                                    </div>
                                </b-list-group-item>
                            </b-list-group>
                        </div>
                        <div class="ml-auto">
                            <b-link href="#" @click.prevent="addInteractiveMediaLink(worksheet, 'worksheet')">
                                <font-awesome-icon :icon="['fad', 'plus-square']"/>
                            </b-link>
                        </div>
                    </div>
                </b-list-group-item>
            </b-list-group>

            <b-alert
                variant="danger"
                :show="!searched && (!linked_worksheets || linked_worksheets && !linked_worksheets.length)"
            >
                No linked printables
            </b-alert>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

export default {
    data: () => ({
        worksheets: [],
        linked_worksheets: [],

        search_term: '',

        searching: false,
        searched: false,

        worksheets_loading: true,
        assets_loading: false
    }),
    props: [
        'article',
        'panel'
    ],
    methods: {
        async getWorksheets() {
            if (!this.article) {
                if (this.panel) {
                    this.linked_worksheets = (this.panel.media || [])
                        .filter(item => item.printable_id && item.type !== 'link' && item.merlin_id === undefined)
                        .map(item => ({
                            ...item,
                            id: item.printable_id,
                            title: item.title || item.name,
                        }))
                }

                return
            }

            try {
                this.worksheets_loading = true
                const response = await this.axios.get('/api/articles/worksheets/' + this.article.article_id)
                if (response.data) {
                    this.linked_worksheets = _.orderBy(response.data, 'created')
                }
                await this.getMerlinData('linked')
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed load worksheets: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.worksheets_loading = false
            }
        },
        async search() {
            if (!this.search_term) {
                return
            }

            try {
                this.searched = true
                this.searching = true

                this.worksheets = []

                const sanitizedSearch = encodeURIComponent(this.search_term)
                const response = await this.axios.get(`/api/articles/search_worksheets/${sanitizedSearch}`)
                if (response.data) {
                    this.worksheets = response.data
                    await this.getMerlinData('searched')
                }
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed load search worksheets: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.searching = false
            }
        },
        async getMerlinData(type) {
            if (this.assets_loading) {
                return
            }
            let merlin_ids = []

            if (type == 'linked') {
                _.each(this.linked_worksheets, worksheet => {
                    if (worksheet.dam_id) {
                        merlin_ids.push(worksheet.dam_id)
                        this.$set(worksheet, 'loading', true)
                    }
                })
            } else {
                _.each(this.worksheets, worksheet => {
                    if (worksheet.dam_id) {
                        merlin_ids.push(worksheet.dam_id)
                        this.$set(worksheet, 'loading', true)
                    }
                })
            }

            if (!merlin_ids.length) {
                return
            }

            try {
                this.assets_loading = true
                merlin_ids = merlin_ids.join(' ')

                const response = await this.axios.get('/api/merlin/search/' + merlin_ids)
                _.each(response.data.assets, asset => {
                    if (type == 'linked') {
                        _.each(this.linked_worksheets, worksheet => {
                            if (asset.merlinid == worksheet.dam_id) {
                                this.$set(worksheet, 'previewpermlink', asset.previewpermlink)
                                this.$set(worksheet, 'thumbpermlink', asset.thumbpermlink)
                                worksheet.loading = false
                            }
                        })
                    } else {
                        _.each(this.worksheets, worksheet => {
                            if (asset.merlinid == worksheet.dam_id) {
                                this.$set(worksheet, 'previewpermlink', asset.previewpermlink)
                                this.$set(worksheet, 'thumbpermlink', asset.thumbpermlink)
                                worksheet.loading = false
                            }
                        })
                    }
                })
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed load merlin data: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.assets_loading = false
            }
        },
        clearSearch() {
            this.search_term = ''
            this.searched = false
            this.searching = false
        },
        addInteractiveMediaLink(item, type) {
            this.$emit('add', item, type)
        }
    },
    mounted() {
        this.getWorksheets()
    }
}
</script>
